import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApi } from "../../services/authApi";
import "../styles/auth.css";
import axios from "axios";
import api from "../../api/axiosConfig"; // same instance used in dental module

type AdditionalDegree = {
  degreeName: string;
  specialization: string;
  institutionName: string;
  certificate?: File | null | string;
};

export default function OnboardingProfile() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);

  const userId = sessionStorage.getItem("onb_userId") || "";
  const emailFromBootstrap = sessionStorage.getItem("onb_email") || "";

  const onboardingProfile = JSON.parse(
    sessionStorage.getItem("onb_profile") || "{}"
  );

    const [form, setForm] = useState({
    firstName: onboardingProfile.firstName || "",
    lastName: onboardingProfile.lastName || "",
    dateOfBirth: "",
    email: emailFromBootstrap,
    mobile: onboardingProfile.phone || "",
    graduateDegree: "Indian MBBS",
    institutionName: "",
    fmgeOrNextRequired: false,
    practiceType: "Institutional",
    practiceInstitutionName: "",
    practiceCity: "",
    smcRegistrationId: "",
    nmcRegistrationId: "",
  });

  const [additionalDegrees, setAdditionalDegrees] = useState<
    AdditionalDegree[]
  >([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [mbbsKeys, setMbbsKeys] = useState<string[]>([]);
  const [healthCertKeys, setHealthCertKeys] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const documentsPreview = useMemo(
    () => documents.map((f) => ({ name: f.name, size: f.size, type: f.type })),
    [documents],
  );

  const addDegree = () => {
    setAdditionalDegrees((p) => [
      ...p,
      {
        degreeName: "",
        specialization: "",
        institutionName: "",
        certificate: null,
      },
    ]);
  };

  const updateDegree = (idx: number, patch: Partial<AdditionalDegree>) => {
    setAdditionalDegrees((p) =>
      p.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    );
  };

  const removeDegree = (idx: number) => {
    setAdditionalDegrees((p) => p.filter((_, i) => i !== idx));
  };

  const onDocsPicked = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files);
    setDocuments((prev) => [...prev, ...next]);
  };

  const removeDoc = (idx: number) => {
    setDocuments((p) => p.filter((_, i) => i !== idx));
  };

  const uploadToS3 = async (
    file: File,
    folder: "mbbs" | "HealthCertificate",
  ) => {
    const presignRes = await api.dataCollectionApi.post("api/s3/presign", {
      id: userId,
      moduleName: `doctors/documents/${userId}/${folder}`,
      originalFilename: file.name,
      contentType: file.type,
      fileSize: file.size,
    });

    const { uploadUrl, key } = presignRes.data;

    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type },
    });

    return key;
  };

  const submit = async () => {
    if (!userId) return alert("Missing userId. Restart onboarding.");

    // build payload exactly like your Auth0 app_metadata structure
    const doctorProfile = {
      ...form,
      dateOfBirth: form.dateOfBirth
        ? new Date(form.dateOfBirth).toISOString()
        : null,
      additionalDegrees: additionalDegrees.map((d, idx) => ({
        degreeName: d.degreeName,
        specialization: d.specialization,
        institutionName: d.institutionName,
        certificateKey: healthCertKeys[idx] || null,
      })),
      // For now, store filenames. Later: S3 keys.
      // documents: documents.map((f) => ({
      //   name: f.name,
      //   path: f.name, // placeholder, later S3 key
      //   mimetype: f.type || "application/octet-stream",
      // })),

      documents: mbbsKeys.map((key) => ({
        name: key.split("/").pop(),
        path: key,
        mimetype: "application/pdf",
      })),
    };

    setLoading(true);
    try {
      // IMPORTANT: send doctorProfile (not profile)
      await authApi.submitDoctorProfile(userId, doctorProfile);

      nav("/onboarding/success");
    } catch (e: any) {
      alert(e?.message || "Failed to submit profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-topbar">
        <div style={{ fontWeight: 800, color: "#174350" }}>QAVACH</div>
        <div style={{ color: "#777" }}>Step 3/3</div>
      </div>

      <div className="auth-accent">Submit Doctor Profile</div>

      <div className="auth-card">
        <h2 className="auth-title">Doctor Profile</h2>
        <p className="auth-sub">
          Uploads are stored as filenames now. Later we’ll replace with AWS S3
          keys.
        </p>

        <div className="auth-grid">
                    <input
            className="auth-input"
            placeholder="First Name"
            value={form.firstName}
            readOnly
          />

          <input
            className="auth-input"
            placeholder="Last Name"
            value={form.lastName}
            readOnly
          />
          <input
            className="auth-input"
            type="date"
            value={form.dateOfBirth}
            onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
          />
                    <input
            className="auth-input"
            placeholder="Email"
            value={form.email}
            readOnly
          />
                    <input
            className="auth-input"
            placeholder="Mobile"
            value={form.mobile}
            readOnly
          />

          <select
            className="auth-input"
            value={form.graduateDegree}
            onChange={(e) =>
              setForm({ ...form, graduateDegree: e.target.value })
            }
          >
            <option>Indian MBBS</option>
            <option>Foreign MBBS</option>
            <option>BDS</option>
            <option>Other</option>
          </select>

          <input
            className="auth-input"
            placeholder="Institution Name"
            value={form.institutionName}
            onChange={(e) =>
              setForm({ ...form, institutionName: e.target.value })
            }
          />

          <div className="auth-check">
            <input
              type="checkbox"
              checked={form.fmgeOrNextRequired}
              onChange={(e) =>
                setForm({ ...form, fmgeOrNextRequired: e.target.checked })
              }
            />
            <span>FMGE / NEXT required</span>
          </div>

          <select
            className="auth-input"
            value={form.practiceType}
            onChange={(e) => setForm({ ...form, practiceType: e.target.value })}
          >
            <option>Institutional</option>
            <option>Private Practice</option>
            <option>Consultant</option>
            <option>Other</option>
          </select>

          <input
            className="auth-input"
            placeholder="Practice Institution Name"
            value={form.practiceInstitutionName}
            onChange={(e) =>
              setForm({ ...form, practiceInstitutionName: e.target.value })
            }
          />

          <input
            className="auth-input"
            placeholder="Practice City"
            value={form.practiceCity}
            onChange={(e) => setForm({ ...form, practiceCity: e.target.value })}
          />

          <input
            className="auth-input"
            placeholder="SMC Registration ID"
            value={form.smcRegistrationId}
            onChange={(e) =>
              setForm({ ...form, smcRegistrationId: e.target.value })
            }
          />

          <input
            className="auth-input"
            placeholder="NMC Registration ID"
            value={form.nmcRegistrationId}
            onChange={(e) =>
              setForm({ ...form, nmcRegistrationId: e.target.value })
            }
          />
        </div>

        <div className="auth-section">
          <div className="auth-section-title">Additional Degrees</div>

          {additionalDegrees.map((d, idx) => (
            <div key={idx} className="auth-box">
              <div className="auth-grid">
                <input
                  className="auth-input"
                  placeholder="Degree Name"
                  value={d.degreeName}
                  onChange={(e) =>
                    updateDegree(idx, { degreeName: e.target.value })
                  }
                />
                <input
                  className="auth-input"
                  placeholder="Specialization"
                  value={d.specialization}
                  onChange={(e) =>
                    updateDegree(idx, { specialization: e.target.value })
                  }
                />
                <input
                  className="auth-input"
                  placeholder="Institution"
                  value={d.institutionName}
                  onChange={(e) =>
                    updateDegree(idx, { institutionName: e.target.value })
                  }
                />
                {/* <input
                  className="auth-input"
                  type="file"
                  onChange={(e) =>
                    updateDegree(idx, {
                      certificate: e.target.files?.[0] || null,
                    })
                  }
                /> */}

                <input
                  className="auth-input"
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    setUploading(true);
                    try {
                      const key = await uploadToS3(file, "HealthCertificate");

                      setHealthCertKeys((prev) => [...prev, key]);

                      updateDegree(idx, {
                        certificate: file,
                      });

                      alert("Degree certificate uploaded");
                    } catch (err) {
                      console.error(err);
                      alert("Upload failed");
                    } finally {
                      setUploading(false);
                    }
                  }}
                />
              </div>

              <button
                className="auth-btn secondary"
                onClick={() => removeDegree(idx)}
                type="button"
              >
                Remove Degree
              </button>
            </div>
          ))}

          <button
            className="auth-btn secondary"
            onClick={addDegree}
            type="button"
          >
            + Add Degree
          </button>
        </div>

        <div className="auth-section">
          <div className="auth-section-title">Documents Upload</div>
          {/* <input
            className="auth-input"
            type="file"
            multiple
            onChange={(e) => onDocsPicked(e.target.files)}
          /> */}

          <input
            className="auth-input"
            type="file"
            multiple
            onChange={async (e) => {
              if (!e.target.files) return;

              setUploading(true);
              try {
                const files = Array.from(e.target.files);
                const keys: string[] = [];

                for (const file of files) {
                  const key = await uploadToS3(file, "mbbs");
                  keys.push(key);
                }

                setMbbsKeys((prev) => [...prev, ...keys]);
                alert("MBBS/NMC documents uploaded successfully");
              } catch (err) {
                console.error(err);
                alert("Upload failed");
              } finally {
                setUploading(false);
              }
            }}
          />

          {documentsPreview.length > 0 && (
            <div className="auth-list">
              {documentsPreview.map((f, idx) => (
                <div key={idx} className="auth-list-item">
                  <div>
                    <div style={{ fontWeight: 700 }}>{f.name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {f.type} • {Math.round(f.size / 1024)} KB
                    </div>
                  </div>
                  <button
                    className="auth-btn secondary"
                    type="button"
                    onClick={() => removeDoc(idx)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="auth-row">
          <button
            className="auth-btn secondary"
            disabled={loading}
            onClick={() => nav("/onboarding/password")}
          >
            Back
          </button>
          <button className="auth-btn" disabled={loading} onClick={submit}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
